// PROMPT 147: empezar un chat nuevo con un cliente por WhatsApp (tambien desde una reserva).
// Solo marcas con el numero conectado, el telefono con codigo de pais, la confirmacion de que el
// cliente acepto que le escribamos y una plantilla aprobada: es lo que Meta pide para escribirle
// a alguien que todavia no escribio.
import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { AlertTriangle, Loader2, MessageCircle } from "lucide-react";
import { colorDeMarca, nombreCorto } from "@/lib/marcasWa";
import ElegirPlantilla from "@/components/agente/ElegirPlantilla";

const digitos = (t) => String(t || "").replace(/\D/g, "");
const primerNombre = (t) => String(t || "").trim().split(/\s+/)[0] || "";

export default function NuevoChatDialog({ open, onOpenChange, marcas, inicial, onAbrir }) {
  const conectadas = useMemo(() => (marcas || []).filter((m) => m && m.conectada), [marcas]);
  const [marcaSlug, setMarcaSlug] = useState("");
  const [telefono, setTelefono] = useState("");
  const [revisado, setRevisado] = useState(""); // el telefono con el que se busco un chat existente
  const [nombre, setNombre] = useState("");
  const [acepto, setAcepto] = useState(false);
  const [buscando, setBuscando] = useState(false);
  const [existente, setExistente] = useState(null);

  // Al abrirse: lo que viene de una reserva (marca, telefono, nombre) ya escrito.
  useEffect(() => {
    if (!open) return;
    const quiere = inicial?.marca_slug;
    const elegible = conectadas.find((m) => m.slug === quiere) || conectadas[0];
    setMarcaSlug(elegible ? elegible.slug : "");
    setTelefono(inicial?.telefono || "");
    setRevisado(inicial?.telefono || "");
    setNombre(inicial?.nombre || "");
    setAcepto(false);
    setExistente(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, inicial]);

  const telefonoValido = digitos(telefono).length >= 8 && digitos(telefono).length <= 15;
  const marca = conectadas.find((m) => m.slug === marcaSlug) || null;
  const nombreMarca = marca ? marca.remitente_nombre || marca.nombre || marca.slug : "";

  // "Ya hay un chat con este numero": se mira al salir del telefono y al cambiar de marca.
  useEffect(() => {
    let vigente = true;
    setExistente(null);
    if (!open || !marcaSlug || digitos(revisado).length < 8) return undefined;
    setBuscando(true);
    api
      .get("/whatsapp/buscar-numero", { params: { marca_slug: marcaSlug, telefono: revisado } })
      .then(({ data }) => {
        if (vigente && data?.conversacion_id) setExistente(data);
      })
      .catch(() => {})
      .finally(() => {
        if (vigente) setBuscando(false);
      });
    return () => {
      vigente = false;
    };
  }, [open, marcaSlug, revisado]);

  const sugeridos = useMemo(
    () => ({
      nombre: primerNombre(nombre),
      marca: nombreMarca,
      ...(inicial?.valores_sugeridos || {}),
      ...(primerNombre(nombre) ? { nombre: primerNombre(nombre) } : {}),
    }),
    [nombre, nombreMarca, inicial]
  );

  const abrirExistente = (id) => {
    onOpenChange(false);
    if (onAbrir) onAbrir(id);
  };

  const alEnviar = async (pedido) => {
    try {
      const { data } = await api.post("/whatsapp/conversaciones/nueva", {
        marca_slug: marcaSlug,
        telefono,
        nombre_cliente: nombre.trim() || null,
        acepto_mensajes: true,
        reserva_id: inicial?.reserva_id || null,
        ...pedido,
      });
      toast.success("Chat nuevo creado y plantilla enviada.");
      abrirExistente(data?.conversacion?.id);
    } catch (e) {
      const d = e.response?.data;
      if (e.response?.status === 409 && d?.conversacion_id) {
        setExistente({
          conversacion_id: d.conversacion_id,
          ventana_abierta: !!d.ventana_abierta,
          archivada: !!d.archivada,
        });
        throw e;
      }
      if (e.response?.status === 502 && d?.conversacion_id) {
        // el chat quedo creado con el mensaje fallido y su explicacion: se abre para verlo
        toast.error(formatApiError(d.detail) || "Meta no entregó la plantilla.");
        abrirExistente(d.conversacion_id);
        return;
      }
      throw e;
    }
  };

  const listo = !!marca && telefonoValido && acepto && !existente;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92dvh] max-w-3xl overflow-y-auto" data-testid="wa-nuevo-chat-dialogo">
        <DialogHeader>
          <DialogTitle>Nuevo chat</DialogTitle>
          <DialogDescription>
            Le escribís al cliente con una plantilla aprobada, desde el número de la marca.
          </DialogDescription>
        </DialogHeader>
        {inicial?.aviso && (
          <p
            className="flex items-start gap-1.5 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800"
            data-testid="wa-nuevo-aviso"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {inicial.aviso}
          </p>
        )}
        {!conectadas.length ? (
          <p className="rounded-md bg-slate-50 px-3 py-6 text-center text-sm text-slate-600" data-testid="wa-nuevo-sin-marcas">
            Ninguna marca tiene el número de WhatsApp conectado todavía. Un administrador lo conecta en Canal.
          </p>
        ) : (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-600">Marca (el número desde el que se escribe)</Label>
              <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Marca">
                {conectadas.map((m) => {
                  const activa = m.slug === marcaSlug;
                  const color = colorDeMarca(m.slug, marcas);
                  return (
                    <button
                      key={m.slug}
                      type="button"
                      role="radio"
                      aria-checked={activa}
                      onClick={() => setMarcaSlug(m.slug)}
                      data-testid={`wa-nuevo-marca-${m.slug}`}
                      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm ${
                        activa ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                      }`}
                    >
                      <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: color }} />
                      {nombreCorto(m.slug, marcas)}
                      {m.wa_display_number ? <span className="text-xs opacity-70">{m.wa_display_number}</span> : null}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="wa-nuevo-telefono" className="text-xs text-slate-600">
                  Teléfono del cliente, con código de país
                </Label>
                <Input
                  id="wa-nuevo-telefono"
                  type="tel"
                  inputMode="tel"
                  value={telefono}
                  onChange={(e) => setTelefono(e.target.value)}
                  onBlur={() => setRevisado(telefono)}
                  placeholder="+57 300 123 4567"
                  className="text-base sm:text-sm"
                  data-testid="wa-nuevo-telefono"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="wa-nuevo-nombre" className="text-xs text-slate-600">
                  Nombre (opcional)
                </Label>
                <Input
                  id="wa-nuevo-nombre"
                  value={nombre}
                  onChange={(e) => setNombre(e.target.value)}
                  placeholder="Ana García"
                  className="text-base sm:text-sm"
                  data-testid="wa-nuevo-nombre"
                />
              </div>
            </div>
            <label className="flex items-start gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={acepto}
                onChange={(e) => setAcepto(e.target.checked)}
                className="mt-0.5 h-4 w-4"
                data-testid="wa-nuevo-acepto"
              />
              <span>El cliente aceptó recibir mensajes de esta marca por WhatsApp</span>
            </label>
            {buscando && (
              <p className="flex items-center gap-1.5 text-xs text-slate-500">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Buscando si ya hay un chat con este número…
              </p>
            )}
            {existente ? (
              <div className="space-y-2 rounded-lg border border-sky-200 bg-sky-50 p-3" data-testid="wa-nuevo-existente">
                <p className="flex items-center gap-1.5 text-sm font-medium text-sky-900">
                  <MessageCircle className="h-4 w-4" /> Ya hay un chat con este número en {nombreMarca}
                </p>
                <p className="text-xs text-sky-800">
                  {existente.archivada
                    ? "Está archivado: al abrirlo vas a ver “Desarchivar” para poder escribirle."
                    : existente.ventana_abierta
                      ? "La ventana de 24 horas está abierta: podés escribirle ahí directamente."
                      : "Pasaron más de 24 horas: ahí mismo le mandás una plantilla."}
                </p>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => abrirExistente(existente.conversacion_id)}
                  data-testid="wa-nuevo-abrir"
                >
                  Abrirlo
                </Button>
              </div>
            ) : listo ? (
              <ElegirPlantilla
                marcaSlug={marcaSlug}
                idioma={inicial?.idioma}
                sugeridos={sugeridos}
                onEnviar={alEnviar}
                textoBoton="Enviar plantilla y abrir el chat"
              />
            ) : (
              <p className="rounded-md bg-slate-50 px-3 py-3 text-sm text-slate-600" data-testid="wa-nuevo-falta">
                {!telefonoValido
                  ? "Escribí el teléfono con código de país para elegir la plantilla."
                  : "Marcá la confirmación de que el cliente aceptó para elegir la plantilla."}
              </p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
